import { fromBaseRecord, fromBaseUuid, type FromBaseRow as Row } from './from-base-context';
import { readItemField } from './item-field-reader';
import { encounterMemberSharedInputs } from './encounter-member-input';
import { absorbInventoryMetadata, mergeInventoryMetadata } from './inventory-shared-catalog';

/** Complete saved status inputs before the member compiler runs. Only raw SELECT
 * closures enter this path; a compiled page catalog is never compiler input. */
export async function memberStatusInputs(base: Row, held: (id: string) => Row | undefined,
    fetch: (ids: readonly string[]) => Promise<void>): Promise<Row> {
    const hosts = [base, ...(Array.isArray(base.encounter_creatures_arr) ? base.encounter_creatures_arr.filter(fromBaseRecord) : [])];
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const wanted = new Set<string>();
    const witnessed = fromBaseRecord(base.definition_videos) && Array.isArray(base.definition_videos.lookupIds)
        ? new Set(base.definition_videos.lookupIds.map(id => String(id).toUpperCase())) : null;
    for (const host of hosts) {
        const statuses = fromBaseRecord(host.statuses) ? host.statuses : {};
        for (const mode of ['target', 'caster']) for (const status of Object.values(statuses[mode] ?? {})) {
            const id = readItemField(status, 'status_item_id');
            if (fromBaseUuid(id) && (!fromBaseRecord(catalog[id.toUpperCase()]) || witnessed && !witnessed.has(id.toUpperCase()))) wanted.add(id.toUpperCase());
        }
    }
    return completeDefinitions(base, wanted, held, fetch);
}

/** Complete only named saved-definition closures, shared by status and prepared-spell deltas. */
export async function memberDefinitionInputs(base: Row, ids: readonly string[], held: (id: string) => Row | undefined,
    fetch: (ids: readonly string[]) => Promise<void>): Promise<Row> {
    return completeDefinitions(base, new Set(ids.map(id => id.toUpperCase()).filter(id => !fromBaseRecord(base.catalog) || !fromBaseRecord(base.catalog[id]))), held, fetch);
}
async function completeDefinitions(base: Row, wanted: ReadonlySet<string>, held: (id: string) => Row | undefined,
    fetch: (ids: readonly string[]) => Promise<void>): Promise<Row> {
    let next = base;
    const attach = (id: string): boolean => {
        const attached = heldMemberDefinitionInputs(next, id, held(id));
        if (!attached) return false;
        next = attached;
        return true;
    };
    const missing = [...wanted].filter(id => !attach(id));
    if (missing.length) {
        // The page's existing exact-id reader owns batching, joins and failure backoff.
        try { await fetch(missing); } catch { /* The compiler retains the unavailable definition's exact name. */ }
        for (const id of missing) attach(id);
    }
    if (wanted.size) {
        const definitions = fromBaseRecord(next.catalog) ? next.catalog : {};
        // A status known missing is still required, never a silently omitted effect. Only a list that actually changes is copied;
        // every other member and list keeps its identity (DIFF-NOT-REBUILD, tgstale28c).
        const kept = (ids: readonly unknown[]): unknown[] | null => {
            const list = ids.filter(id => !wanted.has(String(id).toUpperCase()) && !fromBaseRecord(definitions[String(id).toUpperCase()]));
            return list.length === ids.length ? null : list;
        };
        const own = Array.isArray(next.missing_definition_ids) ? kept(next.missing_definition_ids) : null;
        if (own) next = { ...next, missing_definition_ids: own };
        if (Array.isArray(next.encounter_creatures_arr)) {
            let changed = false;
            const members = next.encounter_creatures_arr.map(member => {
                if (!fromBaseRecord(member) || !Array.isArray(member.missing_definition_ids)) return member;
                const list = kept(member.missing_definition_ids); if (!list) return member;
                changed = true; return { ...member, missing_definition_ids: list };
            });
            if (changed) next = { ...next, encounter_creatures_arr: members };
        }
    }
    return next;
}

/** Synchronous closure adoption also used before a browser publishes its decision. */
export function heldMemberDefinitionInputs(base: Row, id: string, source: Row | undefined, shared?: Row, owned?: WeakSet<object>): Row | undefined {
    if (!source || !fromBaseRecord(source.catalog) || !fromBaseRecord(source.catalog[id])) return;
    const current = fromBaseRecord(base.catalog) ? base.catalog : {}, added: Row = {};
    const visit = (value: unknown): void => {
        if (typeof value === 'string') {
            const ref = value.split('|', 1)[0]!.toUpperCase();
            if (!fromBaseUuid(ref) || current[ref] || added[ref] || !fromBaseRecord(source.catalog)) return;
            const row = source.catalog[ref];
            if (fromBaseRecord(row)) { added[ref] = row; visit(row); }
        } else if (value && typeof value === 'object') for (const child of Object.values(value)) visit(child);
    };
    visit(id);
    const inputs = encounterMemberSharedInputs(source);
    // A definition's SELECT does not own the encounter's inventory or match witnesses.
    for (const key of ['inventory_order', 'enchantment_match_candidates', 'enchantment_match_rows', 'pl_signature']) delete inputs[key];
    if (shared) {
        // Definition rows must travel even when the actor held them already.
        // Common registries need only their additions to the encounter's inputs.
        delete inputs.catalog;
        const next = { ...base, catalog: { ...current, ...added } }; // dnr-clone: allow size=one scoped definition-input accumulator; why=only newly selected catalog and witness entries are copied, never a held encounter or member
        absorbInventoryMetadata(next, inputs, shared, owned);
        return next;
    }
    return mergeInventoryMetadata(base, { ...inputs, catalog: added });
}
